import React, { useEffect, useRef } from 'react'
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import useTreatments from '../../store/treatments/treatmentsContext';
import dayjs from 'dayjs';
import { useTable, useGlobalFilter, usePagination, useFilters, useSortBy } from 'react-table'
import { COLORS, USER_TYPES } from '../../config';
import SecondaryButton from '../../components/Buttons/SecondaryButton';
import ClipboardCheckIcon from '../../components/Icons/outline/ClipboardCheckIcon';
import SecondaryButtonAccent from '../../components/Buttons/SecondaryButtonAccent';
import PlusIcon from '../../components/Icons/solid/PlusIcon';
import AdjustmentsIcon from '../../components/Icons/solid/AdjustmentsIcon';
import DotsIcon from '../../components/Icons/solid/DotsIcon';
import FiltersModal from '../ReactTable/FiltersModal';
import GlobalFilter from '../ReactTable/GlobalFilter';
import TablePagination from '../ReactTable/TablePagination';
import SelectColumnFilter from '../ReactTable/SelectColumnFilter';
import SortAscSmall from '../../components/Icons/solid/SortAscSmall';
import SortDescSmall from '../../components/Icons/solid/SortDescSmall';
import TableRowSubMenu from '../../components/Menus/TableRowSubMenu';
import ClipboardListIcon from '../../components/Icons/outline/ClipboardListIcon';
import useApp from "../../store/app/appContext";
import MoonLoader from 'react-spinners/MoonLoader';
import TrashIcon from '../../components/Icons/outline/TrashIcon'; 
import DeleteTreatmentModal from './DeleteTreatmentModal';
import useOnClickOutside from "../../hooks/useOnClickOutside";

function TreatmentsList() {

  const {
    appState: { user, userAccount },
  } = useApp();

  
  const {
    getAllTreatments,
    deleteTreatment,
    forceDeleteTreatment,
    undoDeleteTreatment,
    getPartnerTreatments,
    getPatientTreatments
  } = useTreatments();


  const ref = useRef();
  const history = useHistory();

  const [isLoading, setIsLoading] = React.useState(true);
  const [showDeleteTreatmentModal, setShowDeleteTreatmentModal] = React.useState(false);
  const [treatmentToDelete, setTreatmentToDelete] = React.useState(null);
  const [ allTreatments, setAllTreatments ] = React.useState([]);

  const [showSubMenu, setShowSubMenu] = React.useState({
    active: false,
    row: null
  })

  useOnClickOutside(
    ref,
    setShowSubMenu.bind(this, {
      active: false,
      row: null,
    })
  );
  
  useEffect(() => {
    if (allTreatments.length) return;
    let source = axios.CancelToken.source();
    if (userAccount.isPartner) {
      getPartnerTreatments(source).then((res) => {
        setAllTreatments(res?.data || []);
      }).catch(() => history.push('/dashboard'));
      setIsLoading(false)
    }
    if (userAccount.isPatient) {
      getPatientTreatments(source).then((res) => {
        setAllTreatments(res?.data || []);
      }).catch(() => history.push('/dashboard'));
      setIsLoading(false)
    }
    if (userAccount.isAdmin) {
      getAllTreatments(source).then((res) => {
        setAllTreatments(res?.data?.data || []);
      }).catch(() => history.push('/dashboard'));
      setIsLoading(false)
    }
    
    return () => {
      source.cancel();
    }
  
  }, [userAccount]);

  const [showFilters, setShowFilters] = React.useState(false)

  const data = React.useMemo(
    () => []
  
  , [allTreatments]);

  if (allTreatments.length && !data.length) {
    allTreatments.map(treatment => {
      return data.push({
        id: treatment._id,
        patient: treatment.patient_id.first_name + ' ' + treatment.patient_id.last_name,
        treatmentType: treatment.treatment_type,
        centerOfExcellence: treatment.center_of_excellence?.name || '',
        createdAt: dayjs(treatment.created_at).format('YYYY-MM-DD'),
        updatedAt: dayjs(treatment.updated_at).format('YYYY-MM-DD'),
        deleted: treatment?.deleted
      })
    })
  };

  const defaultColumn = React.useMemo(() => {
    return {
      Filter: SelectColumnFilter
    }
  }, [])

  const columns = React.useMemo(
    () => [
      {
        Header: 'Patient',
        accessor: 'patient',
      },
      {
        Header: 'Treatment',
        accessor: 'treatmentType',
      },
      {
        Header: 'CoE',
        accessor: 'centerOfExcellence',
      },
      {
        Header: 'Created',
        accessor: 'createdAt',
      },
    ], []
  );

    if (user.user_type === USER_TYPES.STAFF || user.user_type === USER_TYPES.ROOT) columns.push(
      {
        Header: 'Deleted',
        accessor: 'deleted',
      },
    );

  
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    gotoPage,
    prepareRow,
    state : { globalFilter, pageIndex, pageSize },
    preGlobalFilteredRows,
    setGlobalFilter,
    setAllFilters
  } = useTable(
    {
      columns,
      data,
      defaultColumn
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handleRowSubMenuClick = (row) => {
    setShowSubMenu({
      active: !showSubMenu.active,
      row: row
    })
  }

  const handleViewTreatmentDetails = (row) => {
    const treatmentInfo = allTreatments[row];
    setShowSubMenu({
      active: false,
      row: null
    })
    if (userAccount.isAdmin) history.push('/manage-treatments/details', { treatmentInfo });
    if (userAccount.isPatientOrPartner) history.push('/my-treatments/details', { treatmentInfo });
  }
  
  const handleRemoveTreatment = (treatmentInfo) => {
    setTreatmentToDelete(treatmentInfo);
    setShowDeleteTreatmentModal(true);
  }

  const handleDeleteTreatment = (treatmentInfo) => {
    if (treatmentInfo.deleted) {
      forceDeleteTreatment(treatmentInfo.id).then(() => {
        const filteredArray = allTreatments.filter(treatment => treatment._id !== treatmentInfo.id);
        setAllTreatments(filteredArray);
      })
    }
    if (!treatmentInfo.deleted) {
      deleteTreatment(treatmentInfo.id).then(res => {
        const data = res.data.data
        const updatedTreatmentArray = allTreatments.map(treatment => {
          if (treatment._id === data._id) return data;
          return treatment;
        });
        setAllTreatments(updatedTreatmentArray);
      })
    }
    setShowSubMenu({
      active: false,
      row: null
    })
  }

  const handleUndoDeleteTreatment = (treatmentInfo) => {
    undoDeleteTreatment(treatmentInfo.id).then(res => {
      const data = res.data.data
      const updatedTreatmentArray = allTreatments.map(treatment => {
        if (treatment._id === data._id) return data;
        return treatment;
      });
      setAllTreatments(updatedTreatmentArray);
    })
    setShowSubMenu({
      active: false,
      row: null
    })
  }

  return (
    <div className='w-full rounded bg-white shadow'>
      <FiltersModal
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        headerGroups={headerGroups}
        setAllFilters={setAllFilters}
      />
      <DeleteTreatmentModal 
        showDeleteTreatmentModal={showDeleteTreatmentModal}
        setShowDeleteTreatmentModal={setShowDeleteTreatmentModal}
        onDelete={() => handleDeleteTreatment(treatmentToDelete)}
        forceDelete={treatmentToDelete?.deleted ? true : false}
      />
      <div className='p-2 xl:p-4 grid grid-cols-1 gap-y-2 lg:flex lg:items-center w-full'>
        {userAccount.isAdmin && <SecondaryButtonAccent onClick={() => history.push('/manage-treatments/add')} text="Add Treatment" icon={<PlusIcon fill={COLORS.colorPrimary}/>}/>}
        {userAccount.isPartner && <SecondaryButtonAccent onClick={() => history.push('/my-treatments/add')} text="Add Treatment" icon={<PlusIcon fill={COLORS.colorPrimary}/>}/>}
        <SecondaryButton onClick={() => setShowFilters(true)} text="Set filters" className='lg:ml-auto lg:mr-2' icon={<AdjustmentsIcon fill={COLORS.colorSecondary}/>}/>
        <div>
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        </div>
      </div>
      {isLoading && 
      <div className='flex justify-around min-h-170 pt-8'>
        <MoonLoader color={COLORS.colorPrimary}/>
      </div> 
      }
      {!isLoading &&
      <div className='overflow-x-scroll'>
        <table {...getTableProps()} className='w-full mt-4 border-4 border-transparent'>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                <th className='text-left xl:px-2 pb-3 border-b-2 border-gray-100 '/>
                {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())} className='text-secondary xl:px-2 pb-3 border-b-2 border-gray-100 text-left text-base font-semibold'>
                  <span className='flex items-center'>
                  {column.render('Header')}
                  {column.isSorted ? 
                    column.isSortedDesc ? 
                      
                      <SortAscSmall style={'ml-1 h-5 w-5'}/>
                      
                      : <SortDescSmall style={'ml-1 h-5 w-5'} />
                  : ''}
                  </span>
                </th>
                ))}
                <th className='text-left xl:px-2 pb-3 border-b-2 border-gray-100 '></th>
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className=''>
            {page.map(row => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()} className={`  border-b-2 border-gray-100`}>
                  <td  className='text-secondary font-medium xl:px-2 py-3'/>
                  {row.cells.map(cell => {
                    if (cell.column.id === 'name') {
                      return (
                        <td {...cell.getCellProps()} className='text-secondary font-medium xl:px-2 py-3'>
                        {cell.render('Cell')}
                        </td>
                      )
                    };
                    if (cell.column.id === 'city') {
                      return (
                        <td {...cell.getCellProps()} className='text-gray-500 font-medium xl:px-2 py-3'>
                        {cell.render('Cell')}
                        </td>
                      )
                    };
                    if (cell.column.id === 'deleted') {
                      return (
                        <td {...cell.getCellProps()} className='text-gray-500 font-medium xl:px-2 py-3'>
                        {cell.value ? 'Yes' : 'No'}
                        </td>
                      )
                    };
                    return (
                      <td {...cell.getCellProps()} className='text-gray-500  xl:px-2 py-3'>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                  <td className='text-left xl:px-2 relative'>
                    <div ref={ref} className='cursor-pointer flex flex-row-reverse '>
                      <DotsIcon vertical fill={COLORS.colorShade} onClick={() => handleRowSubMenuClick(row.index)}/>
                    </div>
                    {showSubMenu.active && showSubMenu.row === row.index &&
                    <TableRowSubMenu>
                      <ul className='whitespace-nowrap'>
                        <li className='cursor-pointer flex items-center' onClick={() => handleViewTreatmentDetails(row.index)}>
                          <ClipboardListIcon size='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>View treatment details</span>
                        </li>
                        {(row.original.deleted && (userAccount.isStaff || userAccount.isRoot)) &&
                        <li className='cursor-pointer flex items-center mt-2' onClick={() => handleUndoDeleteTreatment(row.original)}>
                          <ClipboardCheckIcon size='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>Activate treatment</span>
                        </li>
                        }
                        {(row.original.deleted && (userAccount.isStaff || userAccount.isRoot)) &&
                        <li className='cursor-pointer flex items-center mt-2' onClick={() => handleRemoveTreatment(row.original)}>
                          <TrashIcon size='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>Permanently delete</span>
                        </li>
                        }
                        {(!row.original.deleted && (userAccount.isStaff || userAccount.isRoot)) &&
                        <li className='cursor-pointer flex items-center mt-2' onClick={() => handleRemoveTreatment(row.original)}>
                          <TrashIcon size='w-5 h-5' stroke={COLORS.colorPrimary}/>
                          <span className='ml-3'>Delete treatment</span>
                        </li>
                        }
                      </ul>
                    </TableRowSubMenu>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      }
      <TablePagination   
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        previousPage={previousPage}
        nextPage={nextPage}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        setPageSize={setPageSize}
        pageSize={pageSize}
        gotoPage={gotoPage}
      />
    </div>
  )
}

export default TreatmentsList; 